import { useState, useEffect } from 'react';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import AssetPreviewModal from '../shared/AssetPreviewModal';
import { HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineDownload, HiOutlineEye } from 'react-icons/hi';

const ASSET_LABELS = {
  blog: 'Blog', newsletter: 'Newsletter', email_sequence: 'Email Seq.', one_pager: 'One-Pager',
  banner: 'Banner', service_page: 'Svc Page', presentation: 'Presentation', marketing_strategy: 'Mktg Strategy',
};
const ASSET_TYPES = Object.keys(ASSET_LABELS);

export default function PMPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [editLimits, setEditLimits] = useState({});
  const [savingLimits, setSavingLimits] = useState(false);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState('');
  const { addToast } = useToast();

  const handleSaveLimits = async () => {
    if (!partnerDetail) return;
    setSavingLimits(true);
    try {
      const { data } = await api.put(`/pm/partners/${partnerDetail.id}/limits`, { limits: editLimits });
      setPartnerDetail(prev => ({ ...prev, limits: data.limits, provisional: Object.values(data.limits).some(v => v > 0) ? false : prev.provisional }));
      // reflect the new asset count/allowance on the row list is not needed; just confirm
      addToast('Limits updated');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to update limits', 'error');
    } finally {
      setSavingLimits(false);
    }
  };

  useEffect(() => {
    api.get('/pm/partners').then(({ data }) => {
      setPartners(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleExpand = async (partnerId) => {
    if (expandedId === partnerId) {
      setExpandedId(null);
      setPartnerDetail(null);
      return;
    }
    try {
      const { data } = await api.get(`/pm/partners/${partnerId}`);
      setPartnerDetail(data);
      setEditLimits(data.limits || {});
      setExpandedId(partnerId);
    } catch {
      addToast('Failed to load partner details', 'error');
    }
  };

  const filtered = partners.filter(p =>
    p.companyName.toLowerCase().includes(search.toLowerCase()) ||
    p.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Partners</h1>
        <p className="text-gray-500">View and manage your assigned partners</p>
      </div>

      <input
        type="text"
        placeholder="Search partners..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-v2-blue outline-none"
      />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Company</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Assets</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Last Asset</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Last Contact</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Contact Type</th>
              <th className="w-10 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No partners assigned.</td>
              </tr>
            ) : filtered.map(partner => (
              <>
                <tr key={partner.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(partner.id)}>
                  <td className="px-4 py-3 text-sm font-medium">{partner.companyName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{partner.email}</td>
                  <td className="px-4 py-3 text-sm">{partner.assetCount || 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {partner.lastAssetDate ? new Date(partner.lastAssetDate).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {partner.lastContactDate ? new Date(partner.lastContactDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {partner.lastContactType ? (
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        partner.lastContactType === 'meeting' ? 'bg-purple-100 text-purple-800' :
                        partner.lastContactType === 'email' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>{partner.lastContactType}</span>
                    ) : 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    {expandedId === partner.id ? <HiOutlineChevronUp className="w-4 h-4" /> : <HiOutlineChevronDown className="w-4 h-4" />}
                  </td>
                </tr>
                {expandedId === partner.id && partnerDetail && (
                  <tr key={`${partner.id}-detail`}>
                    <td colSpan={7} className="px-6 py-4 bg-gray-50">
                      {/* Asset limits editor */}
                      <div className="mb-5">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-sm font-semibold">Monthly Asset Limits</h3>
                          {partnerDetail.provisional && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">locked / awaiting access</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {ASSET_TYPES.map(type => (
                            <div key={type}>
                              <label className="block text-xs text-gray-500 mb-1">{ASSET_LABELS[type]}</label>
                              <input
                                type="number" min="0"
                                value={editLimits[type] ?? 0}
                                onChange={e => setEditLimits(prev => ({ ...prev, [type]: Math.max(0, parseInt(e.target.value) || 0) }))}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-v2-blue outline-none"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-3">
                          <button onClick={handleSaveLimits} disabled={savingLimits}
                            className="px-4 py-1.5 bg-v2-blue text-white rounded-lg text-sm font-medium disabled:opacity-50">
                            {savingLimits ? 'Saving…' : 'Save limits'}
                          </button>
                          {partnerDetail.provisional && <span className="ml-3 text-xs text-gray-500">Setting any limit above 0 unlocks the partner.</span>}
                        </div>
                      </div>

                      {/* Assets */}
                      <h3 className="text-sm font-semibold mb-3">Generated Assets</h3>
                      {partnerDetail.assets?.length > 0 ? (
                        <div className="space-y-1.5">
                          {partnerDetail.assets.map(a => (
                            <div key={a.id} className="flex items-center gap-3 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2">
                              <span className="px-2 py-0.5 text-xs rounded bg-gray-200 font-medium">{ASSET_LABELS[a.assetType] || a.assetType}</span>
                              <span className="text-gray-600 truncate">{a.topic?.title || '-'}</span>
                              <span className="text-gray-400 ml-auto whitespace-nowrap">{new Date(a.createdAt).toLocaleDateString()}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                                a.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>{a.status}</span>
                              {a.status === 'completed' && (
                                <>
                                  <button onClick={() => setPreview(a)} title="Preview" className="p-1.5 text-gray-500 hover:text-v2-blue"><HiOutlineEye className="w-5 h-5" /></button>
                                  <a href={`${API_HOST}/api/pm/assets/${a.id}/download`} title="Download" className="p-1.5 text-gray-500 hover:text-v2-blue"><HiOutlineDownload className="w-5 h-5" /></a>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No assets generated yet.</p>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <AssetPreviewModal
          previewUrl={`${API_HOST}/api/pm/assets/${preview.id}/preview`}
          downloadUrl={`${API_HOST}/api/pm/assets/${preview.id}/download`}
          fileFormat={preview.fileFormat}
          assetLabel={ASSET_LABELS[preview.assetType] || preview.assetType}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
